/*
 * Custom cart drawer behaviour: reserve countdown, upsell
 * carousel and estimated delivery. Loaded alongside cart.js, which keeps
 * ownership of line items, notes and discounts.
 */

const cartCustomRefresh = () => {
  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
};

const fetchCartState = async () => {
  const response = await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') });
  return response.json();
};

if (!customElements.get('cart-reserve-timer')) {
  customElements.define(
    'cart-reserve-timer',
    class CartReserveTimer extends BaseElement {
      get minutes() {
        return parseInt(this.getAttribute('data-minutes'), 10) || 10;
      }

      get storageKey() {
        return this.getAttribute('data-storage-key') || 'cart-reserve-deadline';
      }

      connectedCallback() {
        super.connectedCallback();

        this.countdownElement = this.querySelector('[data-countdown]');

        const unsubscribe = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.restart.bind(this));
        this.registerCleanup(unsubscribe);
        this.registerCleanup(() => clearInterval(this.interval));

        this.start();
      }

      /*
       * The deadline lives in sessionStorage rather than on the element so it
       * survives navigation and the section re-renders that replace this
       * element's markup. Storage throws in some privacy modes, so every access
       * falls back to a deadline held on the constructor for this page load.
       */
      get deadline() {
        let stored = null;

        try {
          stored = sessionStorage.getItem(this.storageKey);
        }
        catch (error) {
          stored = CartReserveTimer.memoryDeadline ?? null;
        }

        const parsed = parseInt(stored, 10);
        if (Number.isFinite(parsed) && parsed > Date.now()) return parsed;

        return this.startCountdown();
      }

      startCountdown() {
        const deadline = Date.now() + this.minutes * 60 * 1000;

        try {
          sessionStorage.setItem(this.storageKey, String(deadline));
        }
        catch (error) {
          CartReserveTimer.memoryDeadline = deadline;
        }

        return deadline;
      }

      tick() {
        const remaining = this.deadline - Date.now();

        if (remaining <= 0) {
          this.startCountdown();
          return;
        }

        const totalSeconds = Math.floor(remaining / 1000);
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');

        if (this.countdownElement) this.countdownElement.textContent = minutes + ':' + seconds;
      }

      start() {
        clearInterval(this.interval);

        this.hidden = false;
        this.tick();
        this.interval = setInterval(this.tick.bind(this), 1000);
      }

      /* Editing the cart is a fresh intent, so the reservation renews with it. */
      restart() {
        this.startCountdown();
        this.start();
      }

      /*
       * The strip is always populated: a countdown that runs out rolls into a
       * new one. Shopify reserves no stock either way, so this is a persuasion
       * device, not a claim about inventory.
       */
    }
  );
}

if (!customElements.get('cart-upsells')) {
  customElements.define(
    'cart-upsells',
    class CartUpsells extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.track = this.querySelector('[data-upsell-track]');
        this.slides = Array.from(this.querySelectorAll('[data-upsell-slide]'));
        this.previousButton = this.querySelector('[data-upsell-prev]');
        this.nextButton = this.querySelector('[data-upsell-next]');

        if (!this.track || this.slides.length === 0) return;

        this.index = 0;

        if (this.previousButton) this.on(this.previousButton, 'click', () => this.go(-1));
        if (this.nextButton) this.on(this.nextButton, 'click', () => this.go(1));
        this.on(this.track, 'scroll', this.onScroll.bind(this), { passive: true });

        for (const slide of this.slides) {
          const select = slide.querySelector('select[data-upsell-variant]');
          const addButton = slide.querySelector('[data-upsell-add]');

          if (select) this.on(select, 'change', () => this.onVariantChange(slide));
          if (addButton) this.on(addButton, 'click', () => this.addToCart(slide, addButton));
        }

        this.updateArrows();
      }

      go(direction) {
        this.index = Math.min(Math.max(this.index + direction, 0), this.slides.length - 1);
        this.slides[this.index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        this.updateArrows();
      }

      onScroll() {
        clearTimeout(this.scrollTimeout);

        this.scrollTimeout = setTimeout(() => {
          const width = this.slides[0]?.offsetWidth || 1;

          this.index = Math.round(this.track.scrollLeft / width);
          this.updateArrows();
        }, 100);
      }

      updateArrows() {
        if (this.previousButton) this.previousButton.disabled = this.index <= 0;
        if (this.nextButton) this.nextButton.disabled = this.index >= this.slides.length - 1;
      }

      onVariantChange(slide) {
        const select = slide.querySelector('select[data-upsell-variant]');
        const priceElement = slide.querySelector('[data-upsell-price]');
        if (!select || !priceElement) return;

        const option = select.options[select.selectedIndex];
        const compareAt = option.getAttribute('data-compare-at-price');
        const compareElement = priceElement.querySelector('.price__compare');
        const currentElement = priceElement.querySelector('.price__current');

        if (currentElement) currentElement.textContent = option.getAttribute('data-price') || '';

        const image = option.getAttribute('data-image');
        const media = slide.querySelector('.cart-upsells__media img');

        if (image && media) {
          media.src = image;
          media.srcset = image;
        }

        if (compareAt) {
          if (compareElement) {
            compareElement.textContent = compareAt;
          }
          else {
            const compare = document.createElement('s');
            compare.className = 'price__compare';
            compare.textContent = compareAt;
            priceElement.prepend(compare);
          }
        }
        else if (compareElement) {
          compareElement.remove();
        }
      }

      async addToCart(slide, button) {
        const variantInput = slide.querySelector('[data-upsell-variant]');
        const variantId = parseInt(variantInput?.value, 10);
        if (!Number.isFinite(variantId)) return;

        button.setAttribute('aria-busy', 'true');

        try {
          const body = JSON.stringify({ items: [{ id: variantId, quantity: 1 }] });
          const response = await fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('json'), body });

          if (!response.ok) throw new Error('Add to cart failed with status ' + response.status);

          const cart = await fetchCartState();
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsells', cart });
          cartCustomRefresh();
        }
        catch (error) {
          console.error('[cart-upsells]', error);
        }
        finally {
          button.removeAttribute('aria-busy');
        }
      }
    }
  );
}

if (!customElements.get('cart-estimated-delivery')) {
  customElements.define(
    'cart-estimated-delivery',
    class CartEstimatedDelivery extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        const target = this.querySelector('[data-delivery-range]');
        const minDays = parseInt(this.getAttribute('data-min-days'), 10);
        const maxDays = parseInt(this.getAttribute('data-max-days'), 10);

        if (!target || !Number.isFinite(minDays) || !Number.isFinite(maxDays)) return;

        const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
          day: 'numeric',
          month: 'short'
        });

        const from = this.addBusinessDays(minDays);
        const to = this.addBusinessDays(maxDays);

        target.textContent = formatter.format(from) + ' – ' + formatter.format(to);
        this.renderFlag();
        this.hidden = false;
      }

      /*
       * A flag emoji is two regional indicator letters, each 0x1F1E6 above the
       * ASCII letter. Windows ships no flag glyphs, so those browsers show the
       * two country letters instead, which still reads correctly.
       */
      renderFlag() {
        const slot = this.querySelector('[data-country-flag]');
        const fallback = this.querySelector('[data-country-fallback]');
        const code = (this.getAttribute('data-country') || '').trim().toUpperCase();

        if (!slot || !/^[A-Z]{2}$/.test(code) || !CartEstimatedDelivery.supportsFlags()) return;

        slot.textContent = String.fromCodePoint(
          ...Array.from(code, (letter) => 0x1f1e6 + letter.charCodeAt(0) - 65)
        );
        slot.hidden = false;
        if (fallback) fallback.hidden = true;
      }

      /*
       * Windows has no flag glyphs, so a flag falls back to two letter boxes
       * that are visibly wider than the single glyph other systems draw. That
       * width difference is the only reliable way to tell, and it is measured
       * once per page rather than per render.
       */
      static supportsFlags() {
        if (typeof CartEstimatedDelivery.flagSupport === 'boolean') {
          return CartEstimatedDelivery.flagSupport;
        }

        CartEstimatedDelivery.flagSupport = false;

        try {
          const context = document.createElement('canvas').getContext('2d');

          if (context) {
            context.font = '16px sans-serif';
            const pair = context.measureText('\u{1F1EA}\u{1F1F8}').width;
            const single = context.measureText('\u{1F1EA}').width;
            CartEstimatedDelivery.flagSupport = pair < single * 2;
          }
        }
        catch (error) {
          CartEstimatedDelivery.flagSupport = false;
        }

        return CartEstimatedDelivery.flagSupport;
      }

      /*
       * Weekends only. Public holidays vary per market and would need a real
       * calendar, so the window is presented as an estimate.
       */
      addBusinessDays(days) {
        const date = new Date();
        let remaining = days;

        while (remaining > 0) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (day !== 0 && day !== 6) remaining -= 1;
        }

        return date;
      }
    }
  );
}
